import { ApiError, listUndeliveredSms, resendSms } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { UndeliveredSmsItem } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { AlertTriangle, CheckCircle2, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// QA R2-06: "#<number> · <plate>" instead of the raw ticket UUID; falls back to
// the short id only when the backend could not resolve the ticket.
export function formatTicketRef(
  item: Pick<UndeliveredSmsItem, 'ticketId' | 'ticketNumber' | 'plate'>,
) {
  if (item.ticketNumber !== undefined && item.plate) return `#${item.ticketNumber} · ${item.plate}`;
  if (item.ticketNumber !== undefined) return `#${item.ticketNumber}`;
  return item.ticketId.slice(0, 8);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UndeliveredSmsPage() {
  const { t } = useTranslation('sms');
  const [items, setItems] = useState<UndeliveredSmsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null); // ticketId being resent

  const load = useCallback(async () => {
    setLoading(true);
    setErrorBanner(null);
    try {
      const response = await listUndeliveredSms();
      setItems(response.items);
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner after 5 s
  useEffect(() => {
    if (!successBanner) return;
    const id = window.setTimeout(() => setSuccessBanner(null), 5000);
    return () => window.clearTimeout(id);
  }, [successBanner]);

  async function handleResend(item: UndeliveredSmsItem) {
    const { ticketId } = item;
    setResending(ticketId);
    setErrorBanner(null);
    try {
      const result = await resendSms(ticketId);
      if (result.dispatched) {
        // The provider (Telnyx) only ACCEPTS the message here — delivery is
        // confirmed (or fails) later via webhook — so say "queued", not "sent".
        setSuccessBanner(t('resend.queued', { ticket: formatTicketRef(item) }));
      } else {
        setErrorBanner(t('resend.dispatchFailed'));
      }
      // Refresh the list so successfully resent items no longer appear as failed
      await load();
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('resend.error') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('resend.unexpectedError') }));
      }
    } finally {
      setResending(null);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {successBanner && (
        <FeedbackBanner
          tone="success"
          message={successBanner}
          onDismiss={() => setSuccessBanner(null)}
        />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('common:states.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-[13px] text-jq-graphite">{t('empty')}</p>
          ) : (
            <ul className="divide-y divide-jq-sand/50 space-y-0">
              {items.map((item) => (
                <UndeliveredSmsRow
                  key={item.id}
                  item={item}
                  onResend={handleResend}
                  isResending={resending === item.ticketId}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Row component ──────────────────────────────────────────────────────────────

interface UndeliveredSmsRowProps {
  item: UndeliveredSmsItem;
  onResend: (item: UndeliveredSmsItem) => Promise<void>;
  isResending: boolean;
}

function UndeliveredSmsRow({ item, onResend, isResending }: UndeliveredSmsRowProps) {
  const { t, i18n } = useTranslation('sms');
  return (
    <li className="flex items-center justify-between gap-4 py-4 text-[13px]">
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-jq-danger" strokeWidth={1.75} />
          <span className="truncate font-medium text-jq-charcoal">{t('row.notDelivered')}</span>
          <span className="rounded bg-jq-danger/10 px-1.5 py-0.5 text-[11px] font-medium text-jq-danger">
            {item.errorCode ?? t('row.unknownError')}
          </span>
        </div>
        <span className="truncate text-jq-graphite">
          {t('row.ticketLabel')} <code className="text-[12px]">{formatTicketRef(item)}</code>
        </span>
        <span className="text-jq-graphite/70">
          {t('row.phoneLabel', {
            phone: item.to,
            date: formatDateTime(item.createdAt, i18n.language),
          })}
        </span>
      </div>

      <Button
        size="sm"
        variant="outline"
        disabled={isResending}
        onClick={() => void onResend(item)}
        aria-label={t('row.resendAriaLabel', { ticketId: formatTicketRef(item) })}
      >
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} />
        {isResending ? t('row.resending') : t('row.resend')}
      </Button>
    </li>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('sms');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : AlertTriangle;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
